import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X, ArrowUpRight, LayoutDashboard, ChevronDown,
  Hospital, Stethoscope, Users, BookOpen, HelpCircle, FileText, Compass,
} from 'lucide-react';
import { CaraLoomLogo } from './CaraLoomLogo';
import { ElderModeToggle } from './ElderModeToggle';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';

const DASH_BY_ROLE = {
  patient: '/patient/dashboard',
  clinician: '/clinician/dashboard',
  agency: '/agency/dashboard',
  discharge_planner: '/planner/dashboard',
  payer: '/payer/dashboard',
  admin: '/admin/dashboard',
};
const dashFor = (role) => DASH_BY_ROLE[role] || '/patient/dashboard';

/* ─────────────────────────────────────────────────────────────────────
   Iter 179 · Simplified public IA aligned to the enterprise buyer:

      Platform  ▾   For Hospitals   For Providers   For Patients & Families
                    Resources ▾   About            Sign In    Request a Demo

   The old "For Providers" active pill on `/` is gone. The persona-forked
   segmented control has been retired. All persona pages remain reachable
   through dedicated top-level links + a clean Platform dropdown.

   "Request a Demo" routes to `/?inquiry=demo` — the LandingPage picks up
   the query and opens the InstitutionInquiryModal automatically. This
   keeps a single demo funnel across every public page without leaking
   internal workflow through a dedicated form page.
   ─────────────────────────────────────────────────────────────── */

const PLATFORM_ITEMS = [
  { label: 'Post-acute placement',       to: '/post-acute-care-platform',       icon: Compass, body: 'Move patients toward appropriate placement.' },
  { label: 'Care transitions',           to: '/care-transitions',               icon: Hospital, body: 'Maintain visibility across the transition.' },
  { label: 'Discharge planning',         to: '/discharge-planning-software',    icon: FileText, body: 'For hospital case-management leadership.' },
];

const RESOURCES_ITEMS = [
  { label: 'Guides',   to: '/guides',  icon: BookOpen,     body: 'Playbooks for care-transition teams.' },
  { label: 'FAQ',      to: '/faq',     icon: HelpCircle,   body: 'Common questions from hospital buyers.' },
  { label: 'Contact',  to: '/contact', icon: Users,        body: 'Talk to the team.' },
];

/* Hover / focus / click aware dropdown. Closes on outside click + Esc. */
function NavDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="whitespace-nowrap inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium text-cn-text-muted hover:text-cn-text transition-colors"
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={testId}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full pt-2 w-[320px] z-40"
          data-testid={`${testId}-menu`}
        >
          <div className="rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 shadow-xl p-2">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-cn-bone-100 dark:hover:bg-white/5 transition-colors"
                data-testid={`${testId}-item-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center flex-none">
                  <it.icon className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-cn-text">{it.label}</div>
                  <div className="text-xs text-cn-text-muted mt-0.5">{it.body}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const PublicNavbar = () => {
  const [open, setOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, token, loading, logout } = useAuth();
  const authResolving = loading && !!token;

  const primaryLinks = [
    { label: 'For Hospitals',            to: '/discharge-planning-software',     icon: Hospital },
    { label: 'For Providers',            to: '/providers',                       icon: Stethoscope },
    { label: 'For Patients & Families',  to: '/care-transitions',                icon: Users },
  ];

  const isActive = (to) => pathname === to;

  const requestDemo = (fromMobile = false) => {
    if (fromMobile) setOpen(false);
    // Iter 179 · If already on `/`, dispatch a window event so the
    // LandingPage opens the InstitutionInquiryModal without a re-mount.
    // Otherwise, navigate to `/?inquiry=demo` — LandingPage picks up
    // the query on mount. Single demo funnel across every public page.
    if (pathname === '/') {
      window.dispatchEvent(new CustomEvent('caraloom:open-inquiry', { detail: { interest: 'demo' } }));
    } else {
      navigate('/?inquiry=demo');
    }
  };

  return (
    <>
    <header
      className="sticky top-0 z-50 cn-glass border-b border-cn-border"
      data-testid="public-navbar"
    >
      <a href="#main-content" className="cn-skip-link" data-testid="skip-to-content-link">
        Skip to main content
      </a>
      <div className="cn-container flex items-center justify-between h-16 md:h-[72px] gap-3">
        <Link to="/" className="flex items-center flex-none" data-testid="navbar-home-link">
          <CaraLoomLogo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center whitespace-nowrap" aria-label="Primary">
          <NavDropdown label="Platform" items={PLATFORM_ITEMS} testId="nav-link-platform" />

          {primaryLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`whitespace-nowrap px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive(l.to)
                  ? 'text-cn-text'
                  : 'text-cn-text-muted hover:text-cn-text'
              }`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              {l.label}
            </Link>
          ))}

          <NavDropdown label="Resources" items={RESOURCES_ITEMS} testId="nav-link-resources" />

          <Link
            to="/about"
            className={`whitespace-nowrap px-3 py-2 rounded-full text-sm font-medium transition-colors ${
              isActive('/about') ? 'text-cn-text' : 'text-cn-text-muted hover:text-cn-text'
            }`}
            data-testid="nav-link-about"
          >
            About
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-2 flex-none">
          <ThemeToggle />
          <ElderModeToggle />
          {user ? (
            <>
              <Link
                to={dashFor(user.role)}
                className="cn-btn cn-btn-accent text-sm"
                data-testid="navbar-dashboard-cta"
              >
                <LayoutDashboard className="w-4 h-4" />
                My dashboard
              </Link>
              <button
                onClick={logout}
                className="text-sm font-medium text-cn-text-muted hover:text-cn-primary px-3 py-2"
                data-testid="navbar-signout-link"
              >
                Sign out
              </button>
            </>
          ) : authResolving ? null : (
            <>
              <Link
                to="/auth"
                className="text-sm font-medium text-cn-text hover:text-cn-primary px-3 py-2"
                data-testid="navbar-signin-link"
              >
                Sign in
              </Link>
              <button
                type="button"
                onClick={() => requestDemo(false)}
                className="cn-btn cn-btn-primary text-sm font-bold"
                data-testid="navbar-request-demo-cta"
              >
                Request a Demo
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className="lg:hidden p-2 rounded-full hover:bg-cn-bone-200"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          aria-expanded={open}
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-cn-border bg-white dark:bg-cn-ink-900" data-testid="navbar-mobile-menu">
          <div className="cn-container py-4 flex flex-col gap-1">
            {/* Platform accordion */}
            <button
              type="button"
              onClick={() => setPlatformOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5"
              data-testid="mobile-nav-platform-toggle"
              aria-expanded={platformOpen}
            >
              Platform
              <ChevronDown className={`w-4 h-4 transition-transform ${platformOpen ? 'rotate-180' : ''}`} />
            </button>
            {platformOpen && (
              <div className="pl-2 pb-1 flex flex-col gap-0.5" data-testid="mobile-nav-platform-menu">
                {PLATFORM_ITEMS.map((it) => (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-cn-text-muted hover:text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5"
                    data-testid={`mobile-nav-platform-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    <it.icon className="w-4 h-4" /> {it.label}
                  </Link>
                ))}
              </div>
            )}

            {primaryLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <l.icon className="w-4 h-4" /> {l.label}
              </Link>
            ))}

            {/* Resources accordion */}
            <button
              type="button"
              onClick={() => setResourcesOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5"
              data-testid="mobile-nav-resources-toggle"
              aria-expanded={resourcesOpen}
            >
              Resources
              <ChevronDown className={`w-4 h-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
            </button>
            {resourcesOpen && (
              <div className="pl-2 pb-1 flex flex-col gap-0.5" data-testid="mobile-nav-resources-menu">
                {RESOURCES_ITEMS.map((it) => (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-cn-text-muted hover:text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5"
                    data-testid={`mobile-nav-resources-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    <it.icon className="w-4 h-4" /> {it.label}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/about"
              onClick={() => setOpen(false)}
              className="px-3 py-2.5 rounded-xl text-sm font-semibold text-cn-text hover:bg-cn-bone-100 dark:hover:bg-white/5"
              data-testid="mobile-nav-about"
            >
              About
            </Link>

            <div className="flex items-center justify-between pt-3 mt-2 border-t border-cn-border">
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <ElderModeToggle />
              </div>
              {user ? (
                <div className="flex items-center gap-2">
                  <Link
                    to={dashFor(user.role)}
                    onClick={() => setOpen(false)}
                    className="cn-btn cn-btn-accent text-sm"
                    data-testid="mobile-dashboard-cta"
                  >
                    <LayoutDashboard className="w-4 h-4" /> My dashboard
                  </Link>
                  <button
                    onClick={() => { setOpen(false); logout(); }}
                    className="text-sm font-medium text-cn-text-muted px-3 py-2"
                    data-testid="mobile-signout-link"
                  >
                    Sign out
                  </button>
                </div>
              ) : authResolving ? null : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/auth"
                    onClick={() => setOpen(false)}
                    className="text-sm font-medium text-cn-text px-3 py-2"
                    data-testid="mobile-nav-signin"
                  >
                    Sign in
                  </Link>
                  <button
                    type="button"
                    onClick={() => requestDemo(true)}
                    className="cn-btn cn-btn-primary text-sm font-bold"
                    data-testid="mobile-nav-request-demo"
                  >
                    Request a Demo <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
      {/* Universal skip-link target */}
      <div id="main-content" tabIndex={-1} aria-hidden="true" />
    </>
  );
};

export default PublicNavbar;
